import { INodeSettingsResponse, NodeTypeCodes } from '@kcftech/prometheus-client';
import { DisabledOptionInfo } from 'components/dropdown/Dropdown';
import { useMemo } from 'react';
import {
    collectionIntervalExceedsUpdateInterval,
    getEffectiveUpdateInterval,
    getNodeSettingValue,
    getSamplingFrequencyAsOption,
    isTriggeredPiezoUnit,
    nodeTypeCodeToSamplingFrequencyValues,
    OptionChild,
} from 'utils/node';

export function useSamplingFrequencyOptions(
    nodeTypeCode: NodeTypeCodes | undefined,
    nodeSettings: INodeSettingsResponse | undefined,
    port?: number,
    isEnveloping?: boolean,
): {
    validOptions: OptionChild<number>[];
    disabledOptions: DisabledOptionInfo[];
} {
    return useMemo(() => {
        const validOpts = (() => {
            if (nodeTypeCode === undefined) {
                return [];
            }

            return nodeTypeCodeToSamplingFrequencyValues(nodeTypeCode).map((value) =>
                getSamplingFrequencyAsOption(
                    value,
                    nodeTypeCode === NodeTypeCodes.KcfIepeAcquisitionUnitV1 ||
                        nodeTypeCode === NodeTypeCodes.KcfVibrationV4,
                ),
            );
        })();

        const piezoCollectionType = getNodeSettingValue(
            nodeSettings?.PiezoCollectionType,
        );
        const updateInterval = getEffectiveUpdateInterval(
            nodeTypeCode,
            piezoCollectionType,
            getNodeSettingValue(nodeSettings?.UpdateInterval),
        );
        const disabledMessage = isTriggeredPiezoUnit(nodeTypeCode, piezoCollectionType)
            ? 'Collection duration must fit within the 10-minute trigger window for triggered Piezo Units. Please adjust to save.'
            : 'Collection duration is greater than update interval. Please adjust to save.';

        const disabledOptions: DisabledOptionInfo[] = [];

        for (const option of validOpts) {
            if (
                collectionIntervalExceedsUpdateInterval({
                    samplingFrequency: option.Value,
                    numberOfSamples: getNodeSettingValue(
                        port !== undefined
                            ? isEnveloping
                                ? nodeSettings?.ConnectedIepeTransducerSettings?.[port]
                                      ?.EnvelopingSettings?.NumberSamples
                                : nodeSettings?.ConnectedIepeTransducerSettings?.[port]
                                      ?.NumberSamples
                            : nodeSettings?.NumberSamples,
                    ),
                    updateInterval,
                })
            ) {
                disabledOptions.push({
                    key: option.Text,
                    message: disabledMessage,
                });
            }
        }

        return {
            validOptions: validOpts,
            disabledOptions,
        };
    }, [
        nodeTypeCode,
        port,
        isEnveloping,
        nodeSettings?.NumberSamples,
        nodeSettings?.UpdateInterval,
        nodeSettings?.PiezoCollectionType,
        nodeSettings?.ConnectedIepeTransducerSettings,
    ]);
}
